import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  Siren,
} from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const prefilled = useRef(false);

  useEffect(() => {
    if (prefilled.current) return;
    const geraet = searchParams.get("geraet");
    const anliegen = searchParams.get("anliegen");
    if (geraet || anliegen) {
      prefilled.current = true;
      setForm((f) => ({
        ...f,
        message: `Gerät: ${geraet || "-"}\nAnliegen: ${anliegen || "-"}\n\n`,
      }));
    }
  }, [searchParams]);

  const set = (key) => (e) => {
    const value = key === "consent" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Dieses Feld ist erforderlich.";
    if (!form.email.trim()) err.email = "Dieses Feld ist erforderlich.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      err.email = "Bitte gib eine gültige E-Mail Adresse ein.";
    if (!form.message.trim()) err.message = "Dieses Feld ist erforderlich.";
    if (!form.consent) err.consent = "Bitte bestätige die Einwilligung.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Bitte fülle alle erforderlichen Felder aus.");
      return;
    }
    setSending(true);
    try {
      await axios.post("/api/contact.php", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
        consent: form.consent,
      });
      setSent(true);
      toast.success("Nachricht wurde erfolgreich gesendet.");
    } catch (err) {
      toast.error("Die Nachricht konnte nicht gesendet werden. Bitte versuche es noch einmal.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", message: "", consent: false });
    setSent(false);
    prefilled.current = false;
  };

  const inputClass = (key) =>
    `w-full bg-[#0D0F13] border rounded-sm px-4 py-3.5 text-gray-100 placeholder:text-gray-600 outline-none transition-colors duration-300 focus:border-[#FFE600] ${
      errors[key] ? "border-red-500/70" : "border-white/15"
    }`;

  return (
    <div data-testid="contact-page">
      {/* HEADER */}
      <section className="relative bg-blueprint pt-40 pb-16 overflow-hidden">
        <div
          className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,230,0,0.1) 0%, transparent 65%)" }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <SectionLabel>Kontakt</SectionLabel>
            <h1 className="mt-5 font-display text-6xl sm:text-7xl lg:text-8xl font-extrabold uppercase text-white leading-[0.9]">
              Sag uns, <span className="text-[#FFE600]">was kaputt ist.</span>
            </h1>
            <p className="mt-6 text-gray-400 text-base sm:text-lg leading-relaxed max-w-2xl">
              Schreib uns deine Nachricht – gerne schon mit Modell und Problem.
              Je genauer die Nachricht, umso schneller können wir helfen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="pb-24 sm:pb-32 pt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-start">
          {/* FORM */}
          <Reveal>
            <div className="glow-card bg-[#111318] rounded-md p-7 sm:p-10">
              {sent ? (
                <motion.div
                  data-testid="contact-success-panel"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center py-14"
                >
                  <CheckCircle2 className="w-14 h-14 text-[#FFE600] mx-auto" strokeWidth={1.5} />
                  <h2 className="mt-6 font-display text-4xl font-bold uppercase text-white">
                    Nachricht gesendet
                  </h2>
                  <p className="mt-4 text-gray-300 leading-relaxed max-w-md mx-auto">
                    Vielen Dank für die Anfrage, wir kümmern uns so schnell wie
                    möglich drum.
                  </p>
                  <p className="mt-2 text-gray-500 text-sm">
                    Behalte bitte deinen Posteingang im Blick (auch der Spam-Ordner).
                  </p>
                  <button
                    data-testid="contact-new-request-button"
                    onClick={reset}
                    className="mt-8 border border-[#FFE600]/60 text-[#FFE600] font-display font-semibold uppercase tracking-wide px-6 py-3 rounded-sm hover:bg-[#FFE600] hover:text-[#090A0C] transition-colors duration-300"
                  >
                    Neue Anfrage stellen
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block font-mono2 text-[11px] tracking-[0.2em] uppercase text-gray-500 mb-2">
                        Name <span className="text-[#FFE600]">*</span>
                      </label>
                      <input
                        id="contact-name"
                        data-testid="contact-name-input"
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Dein Name"
                        className={inputClass("name")}
                      />
                      {errors.name && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-name-error">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block font-mono2 text-[11px] tracking-[0.2em] uppercase text-gray-500 mb-2">
                        E-Mail <span className="text-[#FFE600]">*</span>
                      </label>
                      <input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="deine@mail.de"
                        className={inputClass("email")}
                      />
                      {errors.email && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-email-error">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="contact-phone" className="block font-mono2 text-[11px] tracking-[0.2em] uppercase text-gray-500 mb-2">
                      Telefon <span className="text-gray-600 normal-case tracking-normal">(optional)</span>
                    </label>
                    <input
                      id="contact-phone"
                      data-testid="contact-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="Für schnelle Rückfragen"
                      className={inputClass("phone")}
                    />
                  </div>

                  <div className="mt-5">
                    <label htmlFor="contact-message" className="block font-mono2 text-[11px] tracking-[0.2em] uppercase text-gray-500 mb-2">
                      Deine Nachricht <span className="text-[#FFE600]">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      rows={6}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Deine Nachricht – gerne schon mit Modell und Problem. Je genauer die Nachricht, umso schneller können wir helfen."
                      className={`${inputClass("message")} resize-y`}
                    />
                    {errors.message && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-message-error">{errors.message}</p>}
                  </div>

                  <div className="mt-6">
                    <label className="flex items-start gap-3 cursor-pointer group" data-testid="contact-consent-label">
                      <input
                        type="checkbox"
                        data-testid="contact-consent-checkbox"
                        checked={form.consent}
                        onChange={set("consent")}
                        className="mt-1 w-4 h-4 shrink-0 accent-[#FFE600]"
                      />
                      <span className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                        Ich bin damit einverstanden, dass diese Daten zum Zwecke der
                        Kontaktaufnahme gespeichert und verarbeitet werden. Mir ist
                        bekannt, dass ich meine Einwilligung jederzeit widerrufen
                        kann. <span className="text-[#FFE600]">*</span>
                      </span>
                    </label>
                    {errors.consent && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-consent-error">{errors.consent}</p>}
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={sending}
                    className="group mt-8 w-full bg-[#FFE600] text-[#090A0C] font-display font-bold uppercase tracking-wide text-lg px-6 py-4 rounded-sm flex items-center justify-center gap-3 hover:bg-[#FFF033] hover:scale-[1.01] active:scale-[0.99] transition-transform duration-200 disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {sending ? "Wird gesendet …" : "Anfrage senden"}
                    <Send className={`w-5 h-5 transition-transform duration-300 ${sending ? "animate-pulse" : "group-hover:translate-x-1.5"}`} />
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          {/* INFO */}
          <div className="space-y-5">
            <Reveal delay={0.1}>
              <div className="bg-[#111318] border border-white/10 rounded-md p-8">
                <p className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-gray-500 mb-6">
                  Direkter Draht
                </p>
                <a
                  href="tel:+4915116012024"
                  data-testid="contact-info-phone"
                  className="flex items-center gap-4 group py-3"
                >
                  <span className="w-11 h-11 shrink-0 bg-[#FFE600]/10 border border-[#FFE600]/40 rounded-sm flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#FFE600]" />
                  </span>
                  <span>
                    <span className="block text-xs text-gray-500">Telefon</span>
                    <span className="font-display text-xl font-semibold text-white group-hover:text-[#FFE600] transition-colors">
                      0151 16012024
                    </span>
                  </span>
                </a>
                <a
                  href="https://wa.me/4915116012024?text=Hallo%20HeroPhone!%20Ich%20habe%20eine%20Frage%20zu%20einer%20Reparatur."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-info-whatsapp"
                  className="flex items-center gap-4 group py-3 border-t border-white/10"
                >
                  <span className="w-11 h-11 shrink-0 bg-[#25D366]/10 border border-[#25D366]/40 rounded-sm flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  </span>
                  <span>
                    <span className="block text-xs text-gray-500">WhatsApp</span>
                    <span className="font-display text-xl font-semibold text-white group-hover:text-[#25D366] transition-colors">
                      Direkt chatten
                    </span>
                  </span>
                </a>
                <a
                  href="mailto:info@hero-phone.de"
                  data-testid="contact-info-email"
                  className="flex items-center gap-4 group py-3 border-t border-white/10"
                >
                  <span className="w-11 h-11 shrink-0 bg-[#FFE600]/10 border border-[#FFE600]/40 rounded-sm flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#FFE600]" />
                  </span>
                  <span>
                    <span className="block text-xs text-gray-500">E-Mail</span>
                    <span className="font-display text-xl font-semibold text-white group-hover:text-[#FFE600] transition-colors">
                      info@hero-phone.de
                    </span>
                  </span>
                </a>
                <div className="flex items-center gap-4 py-3 border-t border-white/10" data-testid="contact-info-address">
                  <span className="w-11 h-11 shrink-0 bg-[#FFE600]/10 border border-[#FFE600]/40 rounded-sm flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#FFE600]" />
                  </span>
                  <span>
                    <span className="block text-xs text-gray-500">Adresse</span>
                    <span className="font-display text-xl font-semibold text-white">
                      Dorfstraße 70A · 21775 Odisheim
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-4 py-3 border-t border-white/10" data-testid="contact-info-hours">
                  <span className="w-11 h-11 shrink-0 bg-[#FFE600]/10 border border-[#FFE600]/40 rounded-sm flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#FFE600]" />
                  </span>
                  <span>
                    <span className="block text-xs text-gray-500">Termine</span>
                    <span className="font-display text-xl font-semibold text-white">
                      Nach Vereinbarung – ruf einfach an
                    </span>
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="bg-[#FFE600] rounded-md p-8" data-testid="contact-emergency-banner">
                <div className="flex items-start gap-4">
                  <Siren className="w-8 h-8 text-[#090A0C] shrink-0" strokeWidth={1.75} />
                  <div>
                    <h3 className="font-display text-2xl font-bold uppercase text-[#090A0C]">
                      Eilig? Display heute noch reparieren?
                    </h3>
                    <p className="mt-2 text-[#090A0C]/80 text-sm leading-relaxed">
                      Viele Reparaturen schaffen wir innerhalb von 1 Stunde. Ruf
                      kurz an und sichere dir deinen Express-Slot.
                    </p>
                    <a
                      href="tel:+4915116012024"
                      data-testid="contact-emergency-call-button"
                      className="mt-5 inline-flex items-center gap-2 bg-[#090A0C] text-[#FFE600] font-display font-semibold uppercase tracking-wide px-6 py-3 rounded-sm hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
                    >
                      <Phone className="w-4 h-4" />
                      Jetzt anrufen
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Anfahrt</SectionLabel>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl font-extrabold uppercase text-white leading-[0.92]">
              So findest du <span className="text-[#FFE600]">uns.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div
              className="mt-10 relative rounded-md overflow-hidden border border-white/15 glow-card"
              data-testid="contact-map-card"
            >
              <iframe
                data-testid="contact-map"
                title="Karte: HeroPhone, Dorfstraße 70A, 21775 Odisheim"
                src="https://www.openstreetmap.org/export/embed.html?bbox=8.9227%2C53.6860%2C8.9667%2C53.7080&layer=mapnik&marker=53.6969974%2C8.9447328"
                className="w-full h-[440px] border-0 block"
                style={{ filter: "grayscale(1) invert(0.92) contrast(0.85) brightness(0.9)" }}
                loading="lazy"
              />
              <div className="absolute bottom-5 left-5 right-5 sm:right-auto bg-[#090A0C]/90 backdrop-blur-md border border-[#FFE600]/30 rounded-sm px-6 py-5">
                <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#FFE600]">
                  HeroPhone Werkstatt
                </p>
                <p className="mt-1.5 font-display text-xl font-semibold uppercase text-white">
                  Dorfstraße 70A · 21775 Odisheim
                </p>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Dorfstra%C3%9Fe%2070A%2C%2021775%20Odisheim"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-button"
                  className="mt-3.5 inline-flex items-center gap-2 bg-[#FFE600] text-[#090A0C] font-display font-semibold uppercase tracking-wide px-5 py-2.5 rounded-sm hover:bg-[#FFF033] hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
                >
                  <MapPin className="w-4 h-4" />
                  Route planen
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
